<template>
  <div :class="['price-page', { 'price-page--dialog': inDialog }]">

    <!-- ── Apple Device App Store Banner ──────────── -->
    <div v-if="isAppleDevice" class="apple-banner">
      <div class="apple-banner-inner">
        <div class="apple-banner-icon">W</div>
        <div class="apple-banner-text">
          <p class="apple-banner-title">{{ $t('AppleDeviceWelcome') }}</p>
          <p class="apple-banner-sub">{{ $t('AppleDeviceSubtitle', { device: deviceName }) }}</p>
        </div>
        <a
          :href="appStoreUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="apple-banner-cta"
        >
          <svg class="apple-banner-cta-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          {{ $t('DownloadOnAppStore') }}
        </a>
      </div>
    </div>

    <!-- ── Android App Banner ──────────────────────── -->
    <div class="android-banner">
      <div class="android-banner-inner">
        <div class="android-banner-icon">🤖</div>
        <div class="android-banner-text">
          <p class="android-banner-title">{{ $t('AndroidAppAvailable') }}</p>
          <p class="android-banner-sub">{{ $t('AndroidAppSubtitle') }}</p>
        </div>
        <a
          href="https://wordcloudstudio.com/wordcloudstudio.apk"
          class="android-banner-cta"
        >
          <svg class="android-banner-cta-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/>
          </svg>
          {{ $t('DownloadAPK') }}
        </a>
      </div>
    </div>

    <!-- ── Hero ────────────────────────────────────── -->
    <div v-if="!inDialog" class="price-hero">
      <h1 class="price-hero-title">{{ $t('ChoosePlan') }}</h1>
      <div class="price-hero-badge">
        <span class="price-hero-badge-icon">🎁</span>
        {{ $t("PayHint") }}
        <span class="price-hero-badge-icon">✨</span>
      </div>
    </div>

    <!-- ── Pricing Grid ────────────────────────────── -->
    <div class="pricing-grid">
      <div
        v-for="plan in plans"
        :key="plan.id"
        :class="['pricing-card', { 'pricing-card--featured': plan.isRecommended }]"
      >
        <!-- Recommended badge -->
        <span v-if="plan.isRecommended" class="pricing-card-badge">
          {{ $t('Recommended') }}
        </span>

        <!-- Plan icon -->
        <div class="pricing-card-icon">
          <img :src="VIP" class="pricing-card-icon-img" loading="lazy" alt="" />
        </div>

        <!-- Plan name -->
        <h3 class="pricing-card-name">{{ $t(plan.titleKey) }}</h3>

        <!-- Price -->
        <div class="pricing-card-price">
          <span class="pricing-card-price-amount">
            {{ prices[plan.priceId]?.formattedPrice || plan.price }}
          </span>
          <span class="pricing-card-price-period">{{ $t(plan.timeKey) }}</span>
        </div>

        <!-- Description -->
        <p class="pricing-card-desc">{{ $t(plan.hintKey) }}</p>

        <!-- Feature highlights -->
        <ul v-if="plan.featureKeys" class="pricing-card-features">
          <li v-for="(key, fi) in plan.featureKeys" :key="fi" class="pricing-card-feature">
            <svg class="pricing-card-feature-check" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t(key) }}</span>
          </li>
        </ul>

        <!-- CTA -->
        <el-button
          :type="plan.isRecommended ? 'primary' : 'default'"
          :class="['pricing-card-cta', { 'pricing-card-cta--featured': plan.isRecommended }]"
          :loading="isProcessing"
          @click="openCheckout(plan)"
        >
          {{ $t(plan.buttonKey || 'Subscribe') }}
        </el-button>
      </div>
    </div>

    <!-- ── Currency hint ──────────────────────────── -->
    <p v-if="!inDialog && priceCurrency" class="price-currency-hint">
      {{ $t('PricingRegion', { currency: priceCurrency }) }}
    </p>

    <!-- ── Trust Footer ───────────────────────────── -->
    <div v-if="!inDialog" class="price-footer">
      <div class="price-footer-icons">
        <span class="price-footer-icon">🔒</span>
        <span class="price-footer-icon">💳</span>
      </div>
      <p class="price-footer-text">Secure payment via Paddle · Cancel anytime · 24/7 support</p>
      <p class="price-footer-text mt-1">
        📧 <a href="mailto:emperinter@outlook.com" class="text-indigo-600 hover:underline">emperinter@outlook.com</a>
      </p>
    </div>

  </div>
</template>

<script>
import VIP from '@/assets/vip.png'
import { useSEO } from '@/composables/useSEO'
import { useAppleDevice } from '@/composables/useAppleDevice'

export default {
  name: 'PricePage',
  setup() {
    const { isAppleDevice, deviceName, appStoreUrl } = useAppleDevice()
    return { isAppleDevice, deviceName, appStoreUrl }
  },
  data() {
    return {
      VIP,
      loading: false,
      fetchingPrices: false,
      checkingSubscription: false,
      isProcessing: false,
      prices: {},
      priceCurrency: '',
      plans: [
        {
          id: 'annual',
          priceId: 'pri_01hv37ntpgqgf0nf4vhkzy6st5',
          price: '$16.99',
          titleKey: 'AnnualVIP',
          timeKey: 'AnnualVIPTime',
          hintKey: 'AnnualVIPHint',
          buttonType: 'primary',
          days: 365,
          isRecommended: true,
          featureKeys: ['FeatTemplates', 'FeatFonts', 'FeatHD', 'FeatCustomMask', 'FeatVideoGif', 'FeatPriority']
        },
        {
          id: 'monthly',
          priceId: 'pri_01htq3vq91c18n974hrf84c17q',
          price: '$1.99',
          titleKey: 'MonthlyVIP',
          timeKey: 'MonthlyVIPTime',
          hintKey: 'MonthlyVIPHint',
          buttonType: 'primary',
          days: 31,
          isRecommended: false,
          featureKeys: ['FeatTemplates', 'FeatFonts', 'FeatHD', 'FeatCustomMask', 'FeatVideoGif']
        },
        {
          id: 'daily',
          priceId: 'pri_01htq3wm34engpcy5905fc3hwp',
          price: '$0.59',
          titleKey: 'DailyPlan',
          timeKey: 'DailyPlanTime',
          hintKey: 'DailyPlanHint',
          buttonKey: 'Pay',
          buttonType: 'primary',
          days: 1,
          isRecommended: false,
          featureKeys: ['FeatTemplates', 'FeatFonts', 'FeatHD', 'Feat24h']
        }
      ]
    }
  },
  props: {
    inDialog: Boolean
  },
  async mounted() {
    useSEO({
      title: 'Pricing Plans — WordCloudStudio Word Cloud Generator',
      description: 'Choose the perfect WordCloudStudio plan. Free basic use, affordable daily, monthly, and annual premium plans with custom masks, high-res exports, and more.',
      keywords: 'word cloud pricing, wordcloudstudio plans, word cloud subscription, word art premium',
      canonical: '/price',
      ogTitle: 'WordCloudStudio Pricing — Affordable Word Cloud Plans',
      ogDescription: 'Choose the perfect plan for your word cloud creation needs. Free and premium options available.',
      ogImage: 'https://wordcloudstudio.com/wcdw.png',
      twitterTitle: 'WordCloudStudio Pricing — Affordable Word Cloud Plans',
      twitterDescription: 'Choose the perfect plan for your word cloud creation needs.',
      twitterImage: 'https://wordcloudstudio.com/wcdw.png'
    })
    try {
      if (typeof Paddle !== 'undefined') {
        await this.initializePaddle()
        await this.fetchPrices()
      } else {
        console.warn('Paddle script not loaded')
      }
    } catch (error) {
      console.error('Init failure:', error)
    }
  },
  methods: {
    async initializePaddle() {
      // eslint-disable-next-line no-undef
      Paddle.Initialize({
        token: 'live_08ccd4dc3ab91634d0586e4b147',
        eventCallback: this.handlePaddleEvent
      })
    },
    async fetchPrices() {
      this.fetchingPrices = true
      try {
        const items = this.plans.map(plan => ({ priceId: plan.priceId, quantity: 1 }))
        // eslint-disable-next-line no-undef
        const result = await Paddle.PricePreview({ items })
        this.prices = result.data.details.lineItems.reduce((acc, item) => {
          acc[item.price.id] = {
            formattedPrice: item.formattedTotals.total,
            amount: item.totals.total
          }
          // Paddle auto-detects location.  unitPrice.currencyCode is the base
          // currency (always USD).  The localized currency symbol is in the
          // formatted total string: extract "¥" from "¥3,604", "€" from "€19,99".
          if (!this.priceCurrency) {
            const m = item.formattedTotals.total.match(/^([^\d\s,.]+)/)
            if (m) this.priceCurrency = m[1]
          }
          return acc
        }, {})
      } catch (error) {
        console.warn('Price fetch partial failure', error)
      } finally {
        this.fetchingPrices = false
      }
    },
    async openCheckout(plan) {
      this.isProcessing = true

      if (typeof window.gtag !== 'undefined') {
        const price = this.prices[plan.priceId]?.amount
          ? (this.prices[plan.priceId].amount / 100).toFixed(2)
          : parseFloat(plan.price.replace('$', ''))
        window.gtag('event', 'begin_checkout', {
          currency: 'USD',
          value: parseFloat(price),
          items: [{ item_id: plan.priceId, item_name: plan.titleKey, price: parseFloat(price), quantity: 1 }]
        })
      }

      try {
        // eslint-disable-next-line no-undef
        await Paddle.Checkout.open({
          items: [{ priceId: plan.priceId, quantity: 1 }],
          settings: { displayMode: 'overlay', theme: 'light', locale: this.$i18n.locale }
        })
      } catch (error) {
        this.$message.error(this.$t('CheckoutError'))
      } finally {
        this.isProcessing = false
      }
    },
    handlePaddleEvent(data) {
      if (data.name === 'checkout.completed') {
        const item = data.data.items[0]
        const plan = this.plans.find(p => p.priceId === item.priceId)
        if (plan) {
          const expirationTime = new Date()
          expirationTime.setDate(expirationTime.getDate() + plan.days)
          localStorage.setItem('purchaseStatus', 'purchased')
          localStorage.setItem('expirationTime', expirationTime.toISOString())

          if (typeof window.gtag !== 'undefined') {
            const transactionId = data.data.transactionId || data.data.id || Date.now().toString()
            const value = item.price?.unitPrice?.amount ? (item.price.unitPrice.amount / 100).toFixed(2) : 1.0
            const currency = item.price?.unitPrice?.currencyCode || 'USD'
            const trafficSource = sessionStorage.getItem('traffic_source') || 'direct'

            window.gtag('event', 'conversion', {
              send_to: 'AW-690455913/4ggqCMe9t4sbEOmKnskC',
              value: parseFloat(value), currency, transaction_id: transactionId
            })
            window.gtag('event', 'purchase', {
              transaction_id: transactionId, value: parseFloat(value), currency,
              items: [{ item_id: plan.priceId, item_name: plan.titleKey, price: parseFloat(value), quantity: 1 }]
            })
            window.gtag('event', 'subscription_success', {
              plan_id: plan.id, plan_name: plan.titleKey, plan_days: plan.days,
              value: parseFloat(value), currency, transaction_id: transactionId, referral_source: trafficSource
            })
          }
          this.$message.success(this.$t('PurchaseSuccess'))
          window.location.reload()
        }
      }
    }
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   PricePage — Uses DESIGN.md tokens via glass-theme.css
   Anti-pattern check: no raw hex, no px font-size, no !important
   ═══════════════════════════════════════════════════════════════ */

.price-page {
  padding: var(--space-lg);
  max-width: var(--layout-max-width);
  margin: 0 auto;
}

/* ── Apple Banner (Composed: surface + shadow + radius) ── */
.apple-banner {
  max-width: 42rem;
  margin: 0 auto var(--space-xl);
  border-radius: var(--radius-xl);
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

.apple-banner-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-lg);
}

@media (min-width: 640px) {
  .apple-banner-inner {
    flex-direction: row;
  }
}

.apple-banner-icon {
  flex-shrink: 0;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: var(--radius-lg);
  background: linear-gradient(135deg, var(--color-brand-500), var(--color-brand-700));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-surface-0);
  font-weight: 700;
  font-size: var(--text-heading);
  box-shadow: var(--shadow-lg);
}

.apple-banner-text {
  flex: 1;
  text-align: center;
}

@media (min-width: 640px) {
  .apple-banner-text { text-align: left; }
}

.apple-banner-title {
  color: var(--color-surface-0);
  font-weight: 600;
  font-size: var(--text-body);
  line-height: var(--leading-snug);
}

.apple-banner-sub {
  color: var(--color-text-400);
  font-size: var(--text-caption);
  margin-top: var(--space-2xs);
}

.apple-banner-cta {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-sm) var(--space-lg);
  background: var(--color-surface-0);
  color: var(--color-text-900);
  font-weight: 600;
  font-size: var(--text-caption);
  border-radius: var(--radius-full);
  text-decoration: none;
  box-shadow: var(--shadow-md);
  transition: box-shadow var(--transition-fast);
}

.apple-banner-cta:hover {
  box-shadow: var(--shadow-lg);
}

.apple-banner-cta-icon {
  width: 1.1rem;
  height: 1.1rem;
}

/* ── Android Banner (mirrors Apple banner) ────── */
.android-banner {
  max-width: 42rem;
  margin: 0 auto var(--space-xl);
  border-radius: var(--radius-xl);
  background: linear-gradient(135deg, #1a3a1a 0%, #1a4a2e 50%, #0d5e3a 100%);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

.android-banner-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-lg);
}

@media (min-width: 640px) {
  .android-banner-inner { flex-direction: row; }
}

.android-banner-icon {
  flex-shrink: 0;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: var(--radius-lg);
  background: linear-gradient(135deg, #3ddc84, #01875f);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-heading);
  box-shadow: var(--shadow-lg);
}

.android-banner-text {
  flex: 1;
  text-align: center;
}

@media (min-width: 640px) {
  .android-banner-text { text-align: left; }
}

.android-banner-title {
  color: var(--color-surface-0);
  font-weight: 600;
  font-size: var(--text-body);
  line-height: var(--leading-snug);
}

.android-banner-sub {
  color: rgba(255,255,255,0.7);
  font-size: var(--text-caption);
  margin-top: var(--space-2xs);
}

.android-banner-cta {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-sm) var(--space-lg);
  background: #3ddc84;
  color: #1a3a1a;
  font-weight: 600;
  font-size: var(--text-caption);
  border-radius: var(--radius-full);
  text-decoration: none;
  box-shadow: var(--shadow-md);
  transition: box-shadow var(--transition-fast);
}

.android-banner-cta:hover {
  box-shadow: var(--shadow-lg);
}

.android-banner-cta-icon {
  width: 1.1rem;
  height: 1.1rem;
}

/* ── Hero (Composed: display + badge) ── */
.price-hero {
  text-align: center;
  margin-bottom: var(--space-2xl);
}

.price-hero-title {
  font-size: var(--text-display);
  font-weight: 700;
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-heading);
  color: var(--color-text-900);
  margin: 0 0 var(--space-md);
}

.price-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-sm) var(--space-lg);
  background: linear-gradient(135deg, #fbbf24, #f97316, #ec4899);
  border-radius: var(--radius-full);
  color: var(--color-surface-0);
  font-weight: 700;
  font-size: var(--text-body);
  box-shadow: var(--shadow-md);
  transition: transform var(--transition-medium);
}

.price-hero-badge:hover {
  transform: scale(1.04);
}

.price-hero-badge-icon {
  font-size: var(--text-heading);
  line-height: 1;
}

/* ── Pricing Grid (Composed: grid of cards) ── */
.pricing-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-lg);
  max-width: 56rem;
  margin: 0 auto;
}

/* 2 columns at 640px+ */
@media (min-width: 640px) {
  .pricing-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* 3 columns at 900px+ (only when container is wide enough, not in dialog) */
@media (min-width: 900px) {
  .pricing-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* In dialog: keep single column so all cards are visible without scrolling */
.price-page--dialog {
  max-height: 70vh;
  overflow-y: auto;
  padding: var(--space-xs);
}

.price-page--dialog .pricing-grid {
  grid-template-columns: 1fr;
  max-width: 100%;
}

/* ── Pricing Card (Atomic card + PricingCard composed) ── */
.pricing-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-xl);
  background: var(--color-surface-0);
  border: 1px solid var(--color-surface-200);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--transition-medium), transform var(--transition-medium), border-color var(--transition-medium);
}

.pricing-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.pricing-card--featured {
  border-color: var(--color-brand-600);
  box-shadow: var(--shadow-md), 0 0 0 1px var(--color-brand-600);
}

.pricing-card--featured:hover {
  box-shadow: var(--shadow-xl), 0 0 0 1px var(--color-brand-600);
}

/* Badge */
.pricing-card-badge {
  position: absolute;
  top: -0.75rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-brand-600);
  color: var(--color-surface-0);
  padding: var(--space-2xs) var(--space-sm);
  border-radius: var(--radius-full);
  font-size: var(--text-overline);
  font-weight: 600;
  letter-spacing: var(--tracking-overline);
  text-transform: uppercase;
  white-space: nowrap;
}

/* Icon */
.pricing-card-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: var(--color-surface-50);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-md);
  transition: transform var(--transition-medium);
}

.pricing-card:hover .pricing-card-icon {
  transform: scale(1.1);
}

.pricing-card-icon-img {
  width: 2rem;
  height: 2rem;
  object-fit: contain;
}

/* Name */
.pricing-card-name {
  font-size: var(--text-subheading);
  font-weight: 600;
  color: var(--color-text-900);
  margin: 0 0 var(--space-2xs);
}

/* Price */
.pricing-card-price {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: var(--space-md);
}

.pricing-card-price-amount {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-900);
  line-height: var(--leading-tight);
}

.pricing-card-price-period {
  font-size: var(--text-caption);
  color: var(--color-text-400);
  margin-top: var(--space-3xs);
}

/* Description */
.pricing-card-desc {
  font-size: var(--text-caption);
  color: var(--color-text-400);
  text-align: center;
  margin: 0 0 var(--space-lg);
  min-height: 2.5rem;
}

/* Feature List */
.pricing-card-features {
  list-style: none;
  padding: 0;
  margin: 0 0 var(--space-lg);
  width: 100%;
}

.pricing-card-feature {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-2xs) 0;
  font-size: var(--text-caption);
  color: var(--color-text-600);
}

.pricing-card-feature-check {
  width: 1rem;
  height: 1rem;
  color: var(--color-success);
  flex-shrink: 0;
}

/* CTA */
.pricing-card-cta {
  width: 100%;
  border-radius: var(--radius-md) !important;
  height: 2.75rem !important;
  font-weight: 600 !important;
  font-size: var(--text-body) !important;
}

.pricing-card-cta--featured {
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.35);
}

/* ── Currency hint ── */
.price-currency-hint {
  text-align: center;
  font-size: var(--text-caption);
  color: var(--color-text-400);
  margin: var(--space-lg) 0 calc(-1 * var(--space-md));
}

/* ── Footer ── */
.price-footer {
  margin-top: var(--space-2xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--color-surface-200);
  text-align: center;
}

.price-footer-icons {
  display: flex;
  justify-content: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-xs);
}

.price-footer-icon {
  font-size: 1.1rem;
}

.price-footer-text {
  font-size: var(--text-caption);
  color: var(--color-text-400);
  margin: 0;
}
</style>
